import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '@/contexts/AuthContext';
import { Loader2, ShieldAlert, Fingerprint, Ban, Check, ExternalLink, Users, X, AlertTriangle } from 'lucide-react';
import BackNavButton from '@/components/BackNavButton';
import { toast } from 'sonner';

function relativeTime(iso) {
  if (!iso) return '';
  const sec = Math.floor((new Date() - new Date(iso)) / 1000);
  if (sec < 60) return 'now';
  if (sec < 3600) return `${Math.floor(sec / 60)}m`;
  if (sec < 86400) return `${Math.floor(sec / 3600)}h`;
  if (sec < 604800) return `${Math.floor(sec / 86400)}d`;
  return new Date(iso).toLocaleDateString([], { month: 'short', day: 'numeric' });
}

function StatusPill({ status }) {
  const cfg = {
    open:      { bg: '#FEF2F2', fg: '#B91C1C', label: 'Open' },
    dismissed: { bg: '#F3F4F6', fg: '#6B7280', label: 'Dismissed' },
    actioned:  { bg: '#ECFDF5', fg: '#047857', label: 'Actioned' },
  }[status] || { bg: '#F3F4F6', fg: '#374151', label: status };
  return (
    <span
      className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider"
      style={{ background: cfg.bg, color: cfg.fg }}
    >
      {cfg.label}
    </span>
  );
}

export default function AdminFraud() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [alerts, setAlerts] = useState([]);
  const [statusFilter, setStatusFilter] = useState('open');
  const [expanded, setExpanded] = useState(null); // alert_id currently expanded

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ category: 'alt_account_abuse', limit: '100' });
      if (statusFilter !== 'all') params.append('status', statusFilter);
      const r = await api.get(`/admin/alerts?${params.toString()}`);
      setAlerts(r.data.alerts || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load fraud alerts');
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => { load(); }, [load]);

  const dismiss = async (id) => {
    if (!confirm('Dismiss this alert as a false positive?')) return;
    try {
      await api.post(`/admin/alerts/${id}/dismiss`);
      toast.success('Dismissed');
      setAlerts(a => a.filter(x => x.alert_id !== id));
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to dismiss');
    }
  };

  const banCluster = async (alert) => {
    const count = (alert.related?.cluster_user_ids || []).length;
    if (!confirm(`Ban all ${count} users in this cluster? This soft-deletes their profiles and revokes their sessions.`)) return;
    try {
      const r = await api.post(`/admin/alerts/${alert.alert_id}/ban-cluster`);
      toast.success(`Banned ${r.data.count} users`);
      setAlerts(a => a.filter(x => x.alert_id !== alert.alert_id));
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to ban cluster');
    }
  };

  return (
    <div className="min-h-screen bg-white text-[#0F0F10]" data-testid="admin-fraud-page">
      <div className="max-w-5xl mx-auto px-5 py-6">
        <BackNavButton fallback="/admin" />

        <div className="flex items-center gap-3 mt-2 mb-6">
          <div className="w-11 h-11 rounded-2xl bg-[#FEF2F2] flex items-center justify-center">
            <ShieldAlert size={20} className="text-[#B91C1C]" strokeWidth={2} />
          </div>
          <div>
            <h1 className="font-brand text-[26px] font-[600] leading-tight">Suspicious signups</h1>
            <p className="text-[12.5px] text-[#4B4B52] mt-0.5">
              Alt-account clusters flagged by fingerprint / phone / IP.
            </p>
          </div>
        </div>

        {/* Filter chips */}
        <div className="flex gap-2 mb-4 overflow-x-auto pb-1" data-testid="admin-fraud-filters">
          {['open', 'actioned', 'dismissed', 'all'].map((s) => (
            <button
              key={s}
              onClick={() => setStatusFilter(s)}
              data-testid={`admin-fraud-filter-${s}`}
              className={`px-3 py-1.5 rounded-full text-[11.5px] font-semibold whitespace-nowrap transition-all ${
                statusFilter === s
                  ? 'bg-[#0F0F10] text-white'
                  : 'bg-[#F8F9FA] text-[#4B4B52] hover:bg-black/[0.04]'
              }`}
            >
              {s.charAt(0).toUpperCase() + s.slice(1)}
            </button>
          ))}
          <div className="ml-auto text-[11.5px] text-[#8A8A93] flex items-center gap-1">
            <Users size={11} /> {alerts.length} shown
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="w-6 h-6 animate-spin text-[#B91C1C]" />
          </div>
        ) : alerts.length === 0 ? (
          <div className="text-center py-16 border border-dashed border-black/10 rounded-2xl" data-testid="admin-fraud-empty">
            <Check size={28} className="mx-auto text-emerald-500 mb-3" strokeWidth={2} />
            <p className="text-[15px] font-semibold">No {statusFilter === 'all' ? '' : statusFilter} alerts</p>
            <p className="text-[12.5px] text-[#8A8A93] mt-1">You&apos;re all caught up.</p>
          </div>
        ) : (
          <div className="space-y-3">
            {alerts.map((a) => {
              const isOpen = expanded === a.alert_id;
              const clusterIds = a.related?.cluster_user_ids || [];
              return (
                <div
                  key={a.alert_id}
                  data-testid={`admin-fraud-alert-${a.alert_id}`}
                  className="rounded-2xl border border-black/[0.06] bg-white overflow-hidden"
                >
                  <button
                    onClick={() => setExpanded(isOpen ? null : a.alert_id)}
                    className="w-full flex items-center gap-3 px-4 py-3.5 text-left hover:bg-black/[0.02] transition-colors"
                  >
                    <div className="w-9 h-9 rounded-xl bg-[#FEF2F2] flex items-center justify-center flex-shrink-0">
                      <Fingerprint size={16} className="text-[#B91C1C]" strokeWidth={2} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="text-[13.5px] font-semibold truncate">{a.subject || 'Alt-account cluster'}</p>
                        <StatusPill status={a.status || 'open'} />
                      </div>
                      <p className="text-[11.5px] text-[#8A8A93] mt-0.5">
                        {(a.related?.signup_count || clusterIds.length) || 0} accounts &middot; {relativeTime(a.created_at)}
                        {a.related?.trigger_fingerprint ? ` \u00b7 fp \u2026${a.related.trigger_fingerprint.slice(-8)}` : ''}
                      </p>
                    </div>
                    <span className="text-[11px] text-[#8A8A93] flex-shrink-0">{isOpen ? 'Hide' : 'Details'}</span>
                  </button>

                  {isOpen && (
                    <div className="border-t border-black/[0.05] px-4 py-4 bg-[#FBFBFB]" data-testid={`admin-fraud-details-${a.alert_id}`}>
                      <p className="text-[12.5px] text-[#4B4B52] leading-relaxed whitespace-pre-wrap mb-4">{a.body}</p>

                      {clusterIds.length > 0 && (
                        <div className="mb-4">
                          <p className="text-[10px] uppercase tracking-[0.14em] text-[#8A8A93] font-semibold mb-2">
                            Cluster ({clusterIds.length})
                          </p>
                          <div className="flex flex-wrap gap-1.5">
                            {clusterIds.map((uid) => (
                              <button
                                key={uid}
                                onClick={() => navigate(`/admin/users/${uid}`)}
                                data-testid={`admin-fraud-user-${uid}`}
                                className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] bg-white border border-black/[0.06] hover:border-[#EF4444]/40 hover:text-[#B91C1C] transition-colors font-mono"
                              >
                                {uid.replace('user_', '')}
                                <ExternalLink size={9} strokeWidth={2.4} />
                              </button>
                            ))}
                          </div>
                        </div>
                      )}

                      {(a.status || 'open') === 'open' && (
                        <div className="flex flex-wrap gap-2">
                          <button
                            onClick={() => banCluster(a)}
                            data-testid={`admin-fraud-ban-${a.alert_id}`}
                            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-[#B91C1C] hover:bg-[#991B1B] text-white text-[12px] font-semibold"
                          >
                            <Ban size={13} strokeWidth={2.2} /> Ban all in cluster
                          </button>
                          <button
                            onClick={() => dismiss(a.alert_id)}
                            data-testid={`admin-fraud-dismiss-${a.alert_id}`}
                            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-black/[0.08] hover:bg-black/[0.03] text-[12px] font-semibold text-[#4B4B52]"
                          >
                            <X size={13} strokeWidth={2.2} /> Dismiss (false positive)
                          </button>
                        </div>
                      )}
                      {(a.status === 'actioned') && (
                        <p className="text-[11.5px] text-emerald-700 font-medium inline-flex items-center gap-1">
                          <Check size={12} /> {a.action_summary || 'Actioned'} by {a.actioned_by || 'admin'} &middot; {relativeTime(a.actioned_at)}
                        </p>
                      )}
                      {(a.status === 'dismissed') && (
                        <p className="text-[11.5px] text-zinc-500 font-medium inline-flex items-center gap-1">
                          <AlertTriangle size={12} /> Dismissed by {a.dismissed_by || 'admin'} &middot; {relativeTime(a.dismissed_at)}
                        </p>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
